import { useMemo } from 'react';
import { WORK_NAV, type WorkRail } from './ui';

/**
 * What the chrome draws down the side, from what `/me` said.
 *
 * **This file used to be mostly about studios, and they are gone** (#144). It
 * answered which organisation was being looked at and rewrote the rail around
 * it: a chooser when an account held several and none was named (#88), the
 * organisation's own name as the heading once one was (#98), and two entries —
 * `Statistics` and `Builds` — filtered out until the studio's catalogue made
 * them true (#96, #97). All of that described somebody acting for a company
 * whose games we sold. There is no such company and no `/portal` to navigate.
 *
 * What survives is the part that was never about studios, and it is the part
 * worth keeping: the rail is *derived from what `/me` said*, in one place,
 * rather than being a constant. Two rules run through it:
 *
 *   - **An empty list is how "no rail" is said.** A navigation with nothing in
 *     it and a navigation that should not be drawn are the same fact.
 *   - **Nothing is drawn until `/me` has answered.** The alternative is a rail
 *     of the wrong shape for as long as the request takes, and a navigation
 *     that rearranges itself under the pointer is worse than one that arrives
 *     with the page it describes.
 *
 * Both still matter for the two workplaces left. A creator who has *applied*
 * and not been approved must not be shown a navigation of the board, which the
 * API closes to them (#90); an account that is not staff must not be shown what
 * the eleven admin screens are called (#111).
 */

/*
  `Studio`, `ORGANISATION_PARAM`, `ORGANISATION_SWITCH_PARAM`, `SWITCH_HREF`,
  `NEW_ORGANISATION_PARAM`, `NEW_ORGANISATION_HREF`, `useSwitchRequest`,
  `useNewOrganisationRequest`, `useOrganisationChoice`, `organisationGamesHref`,
  `switchTo`, `DEVELOPER_ACCOUNT`, `DEVELOPER_STUDIO`, `GATED` and
  `studioSection` were all here, and went with the studios (#144).

  Between them they were the organisation switcher: the id in the query string,
  the choice remembered in `localStorage`, the `Your organisations` section that
  named every one an account could act for, and the two catalogue gates. The
  storage key was `goodbuild.organisation`; nothing writes or reads it now, and
  it is left alone in whatever browsers still hold one rather than being swept
  — an unread key costs nothing and a migration to delete it would be a write to
  every visitor's storage for tidiness.
*/

export interface RailInput {
  /**
   * An **approved** creator, not an applicant.
   *
   * The same threshold the top bar uses, and for the same reason: the board is
   * closed until somebody has decided (section 3), so an applied creator
   * reading a link to it is reading a door onto a refusal. What they have
   * instead is the onboarding page, which is where they already are — see #90.
   */
  creator: boolean;
  /**
   * Staff, at the same threshold the top bar's `Admin` entry uses (#111).
   *
   * Every screen in the panel is behind `requireAdmin` on the API, so there is
   * no half-state to draw for: a rail of doors onto a 403 is precisely what #90
   * argued against one audience over, and here it would also be telling
   * somebody who is not staff what the staff screens are called.
   *
   * Unlike `creator` there is no `applied` to distinguish from `active` —
   * nobody applies to be an administrator, so held means active and the two
   * readings cannot come apart. It is spelled the same way anyway, because
   * `router.tsx` derives both roles from `/me` in one place and a different
   * shape here would be a second rule to remember.
   */
  admin: boolean;
  /**
   * Whether `/me` has answered at all.
   *
   * False means *not yet known*, which is not the same as *nothing*, and the
   * honest rail for it is no rail.
   */
  known: boolean;
}

/**
 * What the chrome should draw down the side, for whichever ground it is on.
 *
 * A function of the ground rather than a list, because `Page` is the only thing
 * that knows which ground it is drawing by the time the rail is needed.
 *
 * Pure, and exported separately from the hook below so that the two-state rule
 * can be tested without a renderer — see `test/work-rail.test.ts`. Everything
 * this decides is decided from its argument.
 *
 * **`developers` answers with nothing, and the ground still exists.**
 * `WORK_NAV.developers` is an empty list rather than a missing key, which is
 * the same thing said one layer down; `lib/sites.ts` still resolves the
 * developers' ground so that a workshop surface added later has somewhere to
 * be. See the note on `WORK_NAV`.
 */
export function workRail({ creator, admin, known }: RailInput): WorkRail {
  return (place) => {
    if (!known) return [];
    if (place === 'admin') return admin ? WORK_NAV.admin : [];
    if (place === 'creators') return creator ? WORK_NAV.creators : [];
    return WORK_NAV.developers;
  };
}

/** `workRail`, held still between renders so `Page` is not handed a new one. */
export function useWorkRail({ creator, admin, known }: RailInput): WorkRail {
  return useMemo<WorkRail>(() => workRail({ creator, admin, known }), [creator, admin, known]);
}
